<template>
  <Teleport to="body">
    <div class="modal-overlay create-coupon-modal" @click.stop>
      <div class="modal-container" @click="closeAllDropdowns">
        <div class="modal-header">
          <h2 class="modal-title">Create Coupon</h2>
          <button class="close-btn" type="button" aria-label="Close" @click="handleClose">
            <img :src="closeIcon" alt="" class="close-icon" />
          </button>
        </div>

        <div class="modal-body">
          <div class="form-content">
            <div class="form-group">
              <label class="form-label required">Name</label>
              <div class="input-wrapper" :class="{ error: errors.name }">
                <input
                  v-model="form.name"
                  type="text"
                  class="form-input"
                  placeholder="Enter coupon name"
                  maxlength="200"
                  @input="errors.name = false"
                />
                <img v-if="errors.name" :src="alertIcon" alt="" class="field-error-icon" />
              </div>
            </div>

            <div class="form-group">
              <label class="form-label required">Coupon Code</label>
              <div class="input-wrapper" :class="{ error: errors.code }">
                <input
                  v-model="form.code"
                  type="text"
                  class="form-input"
                  placeholder="Enter coupon code"
                  maxlength="50"
                  @input="errors.code = false"
                />
                <img v-if="errors.code" :src="alertIcon" alt="" class="field-error-icon" />
              </div>
            </div>

            <div class="form-group">
              <label class="form-label required">Coupon Picture</label>
              <div class="upload-container" :class="{ error: errors.picture }">
                <div v-if="!picturePreview" class="upload-area" @click="triggerPictureUpload">
                  <img :src="photoIcon" alt="" class="upload-icon" />
                  <span class="upload-text">Upload</span>
                </div>
                <div v-else class="preview-area">
                  <img :src="picturePreview" alt="" class="preview-image" />
                  <button class="preview-remove" type="button" @click.stop="removePicture">
                    <img :src="closeIcon" alt="" class="preview-remove-icon" />
                  </button>
                </div>
                <input
                  ref="pictureInputRef"
                  type="file"
                  accept=".png,.jpg,.jpeg,.gif,.webp,.bmp,.svg,image/png,image/jpeg,image/gif,image/webp,image/bmp"
                  class="file-input"
                  @change="handlePictureChange"
                />
              </div>
            </div>

            <div class="form-group">
              <label class="form-label required">Discount Type</label>
              <div class="form-row">
                <div class="select-wrapper" :class="{ error: errors.discountType }">
                  <div
                    class="custom-select"
                    :class="{ open: openDropdown === 'discountType' }"
                    @click.stop="toggleDropdown('discountType')"
                  >
                    <span class="select-value">{{ discountTypeLabel }}</span>
                    <img :src="downArrowIcon" alt="" class="select-arrow" />
                  </div>
                  <div v-if="openDropdown === 'discountType'" class="custom-dropdown" @click.stop>
                    <div
                      v-for="option in discountTypeOptions"
                      :key="option.value"
                      class="dropdown-item"
                      :class="{ active: form.discountType === option.value }"
                      @click="selectOption('discountType', option.value)"
                    >
                      {{ option.label }}
                    </div>
                  </div>
                </div>
                <div
                  class="input-wrapper"
                  :class="{
                    error: errors.discountAmount,
                    'has-suffix': form.discountType === 'percentage_off',
                  }"
                >
                  <input
                    v-model="form.discountAmount"
                    type="text"
                    class="form-input"
                    :class="{ 'form-input--with-suffix': form.discountType === 'percentage_off' }"
                    :placeholder="discountAmountPlaceholder"
                    @input="errors.discountAmount = false"
                  />
                  <span v-if="form.discountType === 'percentage_off'" class="input-suffix">OFF</span>
                  <img v-if="errors.discountAmount" :src="alertIcon" alt="" class="field-error-icon" />
                </div>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label required">Minimum Spend Requirement (Before Tax)</label>
              <div class="form-row form-row--with-check">
                <div
                  class="input-wrapper"
                  :class="{ error: errors.minSpend, disabled: form.minSpendNotRequired }"
                >
                  <input
                    v-model="form.minSpend"
                    type="text"
                    class="form-input"
                    placeholder="Enter min. spend amount"
                    :disabled="form.minSpendNotRequired"
                    @input="errors.minSpend = false"
                  />
                  <img v-if="errors.minSpend" :src="alertIcon" alt="" class="field-error-icon" />
                </div>
                <label class="check-option" @click.prevent="toggleMinSpendNotRequired">
                  <input
                    :checked="form.minSpendNotRequired"
                    type="checkbox"
                    class="checkbox-native"
                    tabindex="-1"
                  />
                  <span class="checkbox-ui" :class="{ checked: form.minSpendNotRequired }">
                    <img v-if="form.minSpendNotRequired" :src="checkIcon" alt="" class="check-icon" />
                  </span>
                  <span class="check-option-label">Not required</span>
                </label>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label required">Start of Validity</label>
              <div class="form-row">
                <div class="select-wrapper">
                  <div
                    class="custom-select"
                    :class="{ open: openDropdown === 'startType' }"
                    @click.stop="toggleDropdown('startType')"
                  >
                    <span class="select-value">{{ startTypeLabel }}</span>
                    <img :src="downArrowIcon" alt="" class="select-arrow" />
                  </div>
                  <div v-if="openDropdown === 'startType'" class="custom-dropdown" @click.stop>
                    <div
                      v-for="option in startTypeOptions"
                      :key="option.value"
                      class="dropdown-item"
                      :class="{ active: form.startType === option.value }"
                      @click="selectStartType(option.value)"
                    >
                      {{ option.label }}
                    </div>
                  </div>
                </div>
                <div
                  class="date-field"
                  :class="{ disabled: form.startType === 'immediately', error: errors.startTime }"
                >
                  <input
                    type="text"
                    class="form-input date-input"
                    :value="startTimeDisplay"
                    readonly
                    placeholder="Select date and time"
                    :class="{ 'is-placeholder': !form.startTime }"
                    @click.stop="openDatePicker('start')"
                  />
                  <button
                    type="button"
                    class="calendar-btn"
                    :disabled="form.startType === 'immediately'"
                    @click.stop="openDatePicker('start')"
                  >
                    <img
                      :src="form.startType === 'immediately' ? calendarIconDisabled : calendarIcon"
                      alt=""
                      class="calendar-icon"
                    />
                  </button>
                  <img v-if="errors.startTime" :src="alertIcon" alt="" class="field-error-icon date-error-icon" />
                </div>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label required">End of Validity</label>
              <div class="form-row">
                <div class="select-wrapper">
                  <div
                    class="custom-select"
                    :class="{ open: openDropdown === 'endType' }"
                    @click.stop="toggleDropdown('endType')"
                  >
                    <span class="select-value">{{ endTypeLabel }}</span>
                    <img :src="downArrowIcon" alt="" class="select-arrow" />
                  </div>
                  <div v-if="openDropdown === 'endType'" class="custom-dropdown" @click.stop>
                    <div
                      v-for="option in endTypeOptions"
                      :key="option.value"
                      class="dropdown-item"
                      :class="{ active: form.endType === option.value }"
                      @click="selectEndType(option.value)"
                    >
                      {{ option.label }}
                    </div>
                  </div>
                </div>
                <div
                  class="date-field"
                  :class="{ disabled: form.endType === 'never_expires', error: errors.endTime }"
                >
                  <input
                    type="text"
                    class="form-input date-input"
                    :value="endTimeDisplay"
                    readonly
                    placeholder="Select date and time"
                    :class="{ 'is-placeholder': !form.endTime }"
                    @click.stop="openDatePicker('end')"
                  />
                  <button
                    type="button"
                    class="calendar-btn"
                    :disabled="form.endType === 'never_expires'"
                    @click.stop="openDatePicker('end')"
                  >
                    <img
                      :src="form.endType === 'never_expires' ? calendarIconDisabled : calendarIcon"
                      alt=""
                      class="calendar-icon"
                    />
                  </button>
                  <img v-if="errors.endTime" :src="alertIcon" alt="" class="field-error-icon date-error-icon" />
                </div>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label required">Eligible Services</label>
              <div class="select-wrapper" :class="{ error: errors.eligibleServices }">
                <div
                  class="custom-select"
                  :class="{ open: openDropdown === 'eligibleServices' }"
                  @click.stop="toggleDropdown('eligibleServices')"
                >
                  <span class="select-value" :class="{ 'is-placeholder': form.eligibleServices.length === 0 }">
                    {{ eligibleServicesLabel }}
                  </span>
                  <img :src="downArrowIcon" alt="" class="select-arrow" />
                </div>
                <div
                  v-if="openDropdown === 'eligibleServices'"
                  class="custom-dropdown checkbox-dropdown"
                  @click.stop
                >
                  <div
                    v-if="allServicesOption"
                    class="dropdown-item"
                    :class="{ active: isAllServicesSelected }"
                    @click.stop="toggleAllServices"
                  >
                    <div class="checkbox-row">
                      <div class="checkbox" :class="{ checked: isAllServicesSelected }">
                        <svg v-if="isAllServicesSelected" viewBox="0 0 12 12" fill="none" class="check-svg">
                          <path
                            d="M2 6L5 9L10 3"
                            stroke="white"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </div>
                      <span>{{ allServicesOption.label }}</span>
                    </div>
                  </div>
                  <div
                    v-for="option in eligibleServiceOptionsFiltered"
                    :key="option.value"
                    class="dropdown-item"
                    :class="{ active: form.eligibleServices.includes(option.value) }"
                    @click.stop="toggleService(option.value)"
                  >
                    <div class="checkbox-row">
                      <div class="checkbox" :class="{ checked: form.eligibleServices.includes(option.value) }">
                        <svg
                          v-if="form.eligibleServices.includes(option.value)"
                          viewBox="0 0 12 12"
                          fill="none"
                          class="check-svg"
                        >
                          <path
                            d="M2 6L5 9L10 3"
                            stroke="white"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </div>
                      <span>{{ option.label }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label required">Usage Limit</label>
              <div class="select-wrapper" :class="{ error: errors.usageLimit }">
                <div
                  class="custom-select"
                  :class="{ open: openDropdown === 'usageLimit' }"
                  @click.stop="toggleDropdown('usageLimit')"
                >
                  <span class="select-value">{{ usageLimitLabel }}</span>
                  <img :src="downArrowIcon" alt="" class="select-arrow" />
                </div>
                <div v-if="openDropdown === 'usageLimit'" class="custom-dropdown" @click.stop>
                  <div
                    v-for="option in usageLimitOptions"
                    :key="option.value"
                    class="dropdown-item"
                    :class="{ active: form.usageLimit === option.value }"
                    @click="selectOption('usageLimit', option.value)"
                  >
                    {{ option.label }}
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label required">Eligible Regions</label>
              <div class="select-wrapper" :class="{ error: errors.eligibleRegions }">
                <div
                  class="custom-select"
                  :class="{ open: openDropdown === 'eligibleRegions' }"
                  @click.stop="toggleDropdown('eligibleRegions')"
                >
                  <span class="select-value">{{ eligibleRegionsLabel }}</span>
                  <img :src="downArrowIcon" alt="" class="select-arrow" />
                </div>
                <div v-if="openDropdown === 'eligibleRegions'" class="custom-dropdown" @click.stop>
                  <div
                    v-for="option in eligibleRegionOptions"
                    :key="option.value"
                    class="dropdown-item"
                    :class="{ active: form.eligibleRegions === option.value }"
                    @click="selectOption('eligibleRegions', option.value)"
                  >
                    {{ option.label }}
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label required">Total Redemptions</label>
              <div class="form-row form-row--redemptions">
                <div class="input-wrapper" :class="{ error: errors.maxRedemptions, disabled: form.unlimitedRedemptions }">
                  <input
                    v-model="form.maxRedemptions"
                    type="text"
                    class="form-input"
                    placeholder="Enter maximum redemptions"
                    :disabled="form.unlimitedRedemptions"
                    @input="errors.maxRedemptions = false"
                  />
                  <img v-if="errors.maxRedemptions" :src="alertIcon" alt="" class="field-error-icon" />
                </div>
                <label class="check-option" @click.prevent="toggleUnlimitedRedemptions">
                  <input
                    :checked="form.unlimitedRedemptions"
                    type="checkbox"
                    class="checkbox-native"
                    tabindex="-1"
                  />
                  <span class="checkbox-ui" :class="{ checked: form.unlimitedRedemptions }">
                    <img v-if="form.unlimitedRedemptions" :src="checkIcon" alt="" class="check-icon" />
                  </span>
                  <span class="check-option-label">Unlimited</span>
                </label>
              </div>
            </div>

            <div class="form-group">
              <label class="form-label required">Description</label>
              <div class="textarea-wrapper" :class="{ error: errors.description }">
                <textarea
                  v-model="form.description"
                  class="form-textarea"
                  placeholder="Enter service description"
                  maxlength="5000"
                  @input="errors.description = false"
                />
                <span class="char-count">{{ form.description.length }}/5000</span>
                <img v-if="errors.description" :src="alertIcon" alt="" class="textarea-error-icon" />
              </div>
            </div>
          </div>
        </div>

        <div class="button-group">
          <button class="btn-cancel" type="button" @click="handleClose">Cancel</button>
          <button
            class="btn-submit"
            type="button"
            :disabled="submitting || !copyDataReady"
            @click="handleSubmit"
          >
            <span v-if="submitting" class="spinner" aria-hidden="true"></span>
            <span v-else>Submit</span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <Teleport to="body">
    <div v-if="datePickerOpen" class="date-picker-backdrop" @click.self="closeDatePicker">
      <div class="date-picker-pop" :style="datePickerStyle" @click.stop>
        <CalendarMini
          v-model:month="calMonth"
          v-model:year="calYear"
          :selected-ymd="datePickerYmd"
          class="date-picker-calendar"
          @pick="onPickDate"
        />
        <div class="date-picker-divider" />
        <div class="time-row">
          <div class="time-field" :class="{ 'is-open': timeDropdownOpen === 'hour' }">
            <div class="time-select" @click.stop="toggleTimeDropdown('hour')">
              <span class="time-select-label" :class="{ 'is-placeholder': !datePickerHour }">
                {{ datePickerHour ? formatHour12(datePickerHour) : 'Hour' }}
              </span>
              <img
                :src="downArrowIcon"
                alt=""
                class="time-select-arrow"
                :class="{ 'is-open': timeDropdownOpen === 'hour' }"
              />
            </div>
            <div v-if="timeDropdownOpen === 'hour'" class="time-dropdown" @click.stop>
              <div
                v-for="option in hourOptions"
                :key="option.value"
                class="time-dropdown-item"
                :class="{ active: datePickerHour === option.value }"
                @click="selectTimeValue('hour', option.value)"
              >
                {{ option.label }}
              </div>
            </div>
          </div>
          <div class="time-field" :class="{ 'is-open': timeDropdownOpen === 'minute' }">
            <div class="time-select" @click.stop="toggleTimeDropdown('minute')">
              <span class="time-select-label" :class="{ 'is-placeholder': !datePickerMinute }">
                {{ datePickerMinute || 'Minute' }}
              </span>
              <img
                :src="downArrowIcon"
                alt=""
                class="time-select-arrow"
                :class="{ 'is-open': timeDropdownOpen === 'minute' }"
              />
            </div>
            <div v-if="timeDropdownOpen === 'minute'" class="time-dropdown" @click.stop>
              <div
                v-for="option in minuteSecondOptions"
                :key="`minute-${option}`"
                class="time-dropdown-item"
                :class="{ active: datePickerMinute === option }"
                @click="selectTimeValue('minute', option)"
              >
                {{ option }}
              </div>
            </div>
          </div>
          <div class="time-field" :class="{ 'is-open': timeDropdownOpen === 'second' }">
            <div class="time-select" @click.stop="toggleTimeDropdown('second')">
              <span class="time-select-label" :class="{ 'is-placeholder': !datePickerSecond }">
                {{ datePickerSecond || 'Second' }}
              </span>
              <img
                :src="downArrowIcon"
                alt=""
                class="time-select-arrow"
                :class="{ 'is-open': timeDropdownOpen === 'second' }"
              />
            </div>
            <div v-if="timeDropdownOpen === 'second'" class="time-dropdown" @click.stop>
              <div
                v-for="option in minuteSecondOptions"
                :key="`second-${option}`"
                class="time-dropdown-item"
                :class="{ active: datePickerSecond === option }"
                @click="selectTimeValue('second', option)"
              >
                {{ option }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import type { CreateCouponInitialValues } from '@/api/couponCopy'
import CalendarMini from '@/views/Home/components/CalendarMini.vue'
import {
  DEFAULT_DISCOUNT_TYPES,
  DEFAULT_ELIGIBLE_REGION_OPTIONS,
  DEFAULT_END_VALIDITY_TYPES,
  DEFAULT_START_VALIDITY_TYPES,
  DEFAULT_USAGE_LIMIT_OPTIONS,
  postCouponCreate,
} from '@/api/CouponCreate'
import {
  buildEligibleServices,
  postCouponCenterMeta,
  type CouponMetaOption,
} from '@/api/CouponCenter'
import { isCouponApiSuccess, isCouponMutationSuccess, readCouponApiMessage } from '@/api/couponApi'
import alertIcon from '@/assets/alert.png'
import checkIcon from '@/assets/Path 3@2x.png'
import calendarIcon from '@/assets/编组 2@2x.png'
import calendarIconDisabled from '@/assets/编组 2@2x (2).png'
import closeIcon from '@/assets/Fill 1@2x.png'
import downArrowIcon from '@/assets/downarrow@2x.png'
import photoIcon from '@/assets/photo.png'

type DropdownKey =
  | 'discountType'
  | 'startType'
  | 'endType'
  | 'eligibleServices'
  | 'usageLimit'
  | 'eligibleRegions'

type DatePickerTarget = 'start' | 'end'
type TimeDropdownField = 'hour' | 'minute' | 'second'

const props = defineProps<{
  initialValues?: CreateCouponInitialValues | null
  copyMode?: boolean
  /** 父页面已有 meta 选项时传入，避免弹窗再打 /coupon/meta */
  eligibleServiceOptions?: CouponMetaOption[] | null
}>()

const emit = defineEmits<{
  close: []
  saved: []
  failed: []
}>()

const showToast = inject<(type: 'success' | 'warning' | 'error', message: string) => void>(
  'showToast',
  () => undefined,
)

const discountTypeOptions = DEFAULT_DISCOUNT_TYPES
const startTypeOptions = DEFAULT_START_VALIDITY_TYPES
const endTypeOptions = DEFAULT_END_VALIDITY_TYPES
/** Local options list (prop is also named eligibleServiceOptions — keep names distinct for vue/no-dupe-keys). */
const eligibleServiceOptionsList = ref<CouponMetaOption[]>([])
const usageLimitOptions = DEFAULT_USAGE_LIMIT_OPTIONS
const eligibleRegionOptions = DEFAULT_ELIGIBLE_REGION_OPTIONS

function isAllServicesOptionValue(value: string): boolean {
  const normalized = value.trim().toLowerCase()
  return normalized === 'all' || normalized === 'all_services' || normalized === '2'
}

function resolveDefaultEligibleServices(): string[] {
  const allOption = eligibleServiceOptionsList.value.find((option) =>
    isAllServicesOptionValue(option.value),
  )
  if (allOption) return [allOption.value]
  const first = eligibleServiceOptionsList.value[0]
  return first ? [first.value] : ['all']
}

const form = reactive({
  name: '',
  code: '',
  discountType: 'fixed_amount',
  discountAmount: '',
  minSpend: '',
  minSpendNotRequired: true,
  startType: 'immediately',
  startTime: '',
  endType: 'specific_time',
  endTime: '',
  eligibleServices: ['all_services'] as string[],
  usageLimit: 'one_per_account_or_address',
  eligibleRegions: 'one_per_account_or_address',
  maxRedemptions: '',
  unlimitedRedemptions: true,
  description: '',
})

const errors = reactive({
  name: false,
  code: false,
  picture: false,
  discountType: false,
  discountAmount: false,
  minSpend: false,
  startTime: false,
  endTime: false,
  eligibleServices: false,
  usageLimit: false,
  eligibleRegions: false,
  maxRedemptions: false,
  description: false,
})

const submitting = ref(false)
const copyDataReady = computed(() => !props.copyMode || Boolean(props.initialValues))
const openDropdown = ref<DropdownKey | null>(null)
const pictureInputRef = ref<HTMLInputElement | null>(null)
const pictureFile = ref<File | null>(null)
const picturePreview = ref('')
/** 复制带入的服务端图片 path（无新上传文件时提交） */
const picturePathFromSource = ref('')

const datePickerOpen = ref(false)
const datePickerTarget = ref<DatePickerTarget>('end')
const datePickerStyle = ref<Record<string, string>>({})
const datePickerYmd = ref('')
const calMonth = ref(new Date().getMonth())
const calYear = ref(new Date().getFullYear())
const datePickerHour = ref('')
const datePickerMinute = ref('')
const datePickerSecond = ref('')
const timeDropdownOpen = ref<TimeDropdownField | null>(null)

function formatHour12(hour24: string): string {
  const hour = Number.parseInt(hour24, 10)
  if (Number.isNaN(hour)) return hour24
  const period = hour < 12 ? 'AM' : 'PM'
  const hour12 = hour % 12 === 0 ? 12 : hour % 12
  return `${String(hour12).padStart(2, '0')} ${period}`
}

const hourOptions = Array.from({ length: 24 }, (_, index) => {
  const value = String(index).padStart(2, '0')
  return { value, label: formatHour12(value) }
})
const minuteSecondOptions = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, '0'))

const eligibleServiceOptionsFiltered = computed(() =>
  eligibleServiceOptionsList.value.filter((option) => !isAllServicesOptionValue(option.value)),
)

const allServicesOption = computed(
  () =>
    eligibleServiceOptionsList.value.find((option) => isAllServicesOptionValue(option.value)) ??
    null,
)

const isAllServicesSelected = computed(() =>
  form.eligibleServices.some((value) => isAllServicesOptionValue(value)),
)

const discountTypeLabel = computed(
  () => discountTypeOptions.find((o) => o.value === form.discountType)?.label ?? 'Fixed Amount',
)

const discountAmountPlaceholder = computed(() =>
  form.discountType === 'percentage_off' ? 'Enter percentage off' : 'Enter discount amount',
)

const startTypeLabel = computed(
  () => startTypeOptions.find((o) => o.value === form.startType)?.label ?? 'Immediately',
)

const endTypeLabel = computed(
  () => endTypeOptions.find((o) => o.value === form.endType)?.label ?? 'Until a specific time',
)

const eligibleServicesLabel = computed(() => {
  if (form.eligibleServices.length === 0) return 'Select services'
  if (isAllServicesSelected.value) {
    return allServicesOption.value?.label ?? 'All Services'
  }
  return eligibleServiceOptionsList.value
    .filter((o) => form.eligibleServices.includes(o.value))
    .map((o) => o.label)
    .join(', ')
})

const usageLimitLabel = computed(
  () =>
    usageLimitOptions.find((o) => o.value === form.usageLimit)?.label ??
    'One-time use per account or address',
)

const eligibleRegionsLabel = computed(
  () =>
    eligibleRegionOptions.find((o) => o.value === form.eligibleRegions)?.label ??
    'One-time use per account or address',
)

function formatDateTimeDisplay(iso: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return date.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  })
}

const startTimeDisplay = computed(() => formatDateTimeDisplay(form.startTime))
const endTimeDisplay = computed(() => formatDateTimeDisplay(form.endTime))

function closeAllDropdowns(): void {
  openDropdown.value = null
  timeDropdownOpen.value = null
}

function toggleTimeDropdown(field: TimeDropdownField): void {
  timeDropdownOpen.value = timeDropdownOpen.value === field ? null : field
}

function selectTimeValue(field: TimeDropdownField, value: string): void {
  if (field === 'hour') datePickerHour.value = value
  if (field === 'minute') datePickerMinute.value = value
  if (field === 'second') datePickerSecond.value = value
  timeDropdownOpen.value = null
  applyDatePickerIfComplete()
}

function toggleDropdown(key: DropdownKey): void {
  openDropdown.value = openDropdown.value === key ? null : key
}

function selectOption(key: keyof typeof form, value: string): void {
  ;(form as Record<string, unknown>)[key] = value
  openDropdown.value = null
  if (key in errors) {
    ;(errors as Record<string, boolean>)[key] = false
  }
}

function selectStartType(value: string): void {
  form.startType = value
  openDropdown.value = null
  errors.startTime = false
  if (value === 'immediately') {
    form.startTime = ''
  }
}

function selectEndType(value: string): void {
  form.endType = value
  openDropdown.value = null
  errors.endTime = false
  if (value === 'never_expires') {
    form.endTime = ''
  }
}

function toggleMinSpendNotRequired(): void {
  form.minSpendNotRequired = !form.minSpendNotRequired
  if (form.minSpendNotRequired) {
    form.minSpend = ''
    errors.minSpend = false
  }
}

function toggleUnlimitedRedemptions(): void {
  form.unlimitedRedemptions = !form.unlimitedRedemptions
  if (form.unlimitedRedemptions) {
    form.maxRedemptions = ''
    errors.maxRedemptions = false
  }
}

function toggleAllServices(): void {
  if (isAllServicesSelected.value) {
    form.eligibleServices = []
  } else {
    form.eligibleServices = resolveDefaultEligibleServices()
  }
  errors.eligibleServices = false
}

function toggleService(value: string): void {
  if (isAllServicesSelected.value) {
    form.eligibleServices = [value]
  } else if (form.eligibleServices.includes(value)) {
    form.eligibleServices = form.eligibleServices.filter((item) => item !== value)
  } else {
    form.eligibleServices = [...form.eligibleServices, value]
  }
  errors.eligibleServices = false
}

function triggerPictureUpload(): void {
  pictureInputRef.value?.click()
}

function handlePictureChange(event: Event): void {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0] ?? null
  errors.picture = false

  if (!file) {
    pictureFile.value = null
    picturePreview.value = ''
    return
  }

  const isImage =
    file.type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(file.name)
  if (!isImage) {
    showToast('warning', 'Please upload an image file')
    input.value = ''
    return
  }

  pictureFile.value = file
  picturePreview.value = URL.createObjectURL(file)
  picturePathFromSource.value = ''
}

function removePicture(): void {
  pictureFile.value = null
  picturePreview.value = ''
  picturePathFromSource.value = ''
  if (pictureInputRef.value) pictureInputRef.value.value = ''
}

function openDatePicker(target: DatePickerTarget): void {
  if (target === 'start' && form.startType === 'immediately') return
  if (target === 'end' && form.endType === 'never_expires') return
  datePickerTarget.value = target
  const current = target === 'start' ? form.startTime : form.endTime
  if (current) {
    const date = new Date(current)
    calMonth.value = date.getMonth()
    calYear.value = date.getFullYear()
    datePickerYmd.value = current.slice(0, 10)
    datePickerHour.value = String(date.getHours()).padStart(2, '0')
    datePickerMinute.value = String(date.getMinutes()).padStart(2, '0')
    datePickerSecond.value = String(date.getSeconds()).padStart(2, '0')
  } else {
    const now = new Date()
    calMonth.value = now.getMonth()
    calYear.value = now.getFullYear()
    datePickerYmd.value = ''
    datePickerHour.value = ''
    datePickerMinute.value = ''
    datePickerSecond.value = ''
  }
  datePickerStyle.value = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    zIndex: '100003',
  }
  datePickerOpen.value = true
  timeDropdownOpen.value = null
  closeAllDropdowns()
}

function onPickDate(ymd: string): void {
  datePickerYmd.value = ymd
  timeDropdownOpen.value = null
  applyDatePickerIfComplete()
}

function applyDatePickerIfComplete(): void {
  if (!datePickerYmd.value) return
  if (!datePickerHour.value || !datePickerMinute.value || !datePickerSecond.value) return

  const iso = `${datePickerYmd.value}T${datePickerHour.value.padStart(2, '0')}:${datePickerMinute.value.padStart(2, '0')}:${datePickerSecond.value.padStart(2, '0')}`
  if (datePickerTarget.value === 'start') {
    form.startTime = iso
    errors.startTime = false
  } else {
    form.endTime = iso
    errors.endTime = false
  }
  closeDatePicker()
}

function closeDatePicker(): void {
  datePickerOpen.value = false
  timeDropdownOpen.value = null
}

function handleClose(): void {
  emit('close')
}

function validateForm(): boolean {
  Object.keys(errors).forEach((key) => {
    errors[key as keyof typeof errors] = false
  })

  const missing: string[] = []
  const mark = (key: keyof typeof errors, label: string) => {
    errors[key] = true
    if (!missing.includes(label)) missing.push(label)
  }

  if (!form.name.trim()) mark('name', 'Name')
  if (!form.code.trim()) mark('code', 'Coupon Code')
  if (!pictureFile.value && !picturePreview.value && !picturePathFromSource.value) {
    mark('picture', 'Coupon Picture')
  }
  if (!form.discountType.trim()) mark('discountType', 'Discount Type')
  if (!form.discountAmount.trim()) {
    mark('discountAmount', 'Discount Amount')
  } else {
    const amount = Number.parseFloat(form.discountAmount.trim())
    if (!Number.isFinite(amount) || amount <= 0) {
      mark('discountAmount', 'Discount Amount')
    } else if (form.discountType === 'percentage_off' && amount > 100) {
      mark('discountAmount', 'Discount Amount (1-100)')
    }
  }
  if (!form.minSpendNotRequired) {
    if (!form.minSpend.trim()) {
      mark('minSpend', 'Minimum Spend Requirement')
    } else {
      const minSpend = Number.parseFloat(form.minSpend.trim())
      if (!Number.isFinite(minSpend) || minSpend < 0) {
        mark('minSpend', 'Minimum Spend Requirement')
      }
    }
  }
  if (form.startType === 'specific_time' && !form.startTime.trim()) {
    mark('startTime', 'Start of Validity')
  }
  if (form.endType === 'specific_time' && !form.endTime.trim()) {
    mark('endTime', 'End of Validity')
  }
  if (
    form.startType === 'specific_time' &&
    form.endType === 'specific_time' &&
    form.startTime.trim() &&
    form.endTime.trim()
  ) {
    const startMs = Date.parse(form.startTime)
    const endMs = Date.parse(form.endTime)
    if (Number.isFinite(startMs) && Number.isFinite(endMs) && startMs >= endMs) {
      mark('endTime', 'End of Validity (must be after start)')
    }
  }
  if (form.eligibleServices.length === 0) mark('eligibleServices', 'Eligible Services')
  if (
    !form.usageLimit.trim() ||
    !usageLimitOptions.some((option) => option.value === form.usageLimit)
  ) {
    mark('usageLimit', 'Usage Limit')
  }
  if (!form.eligibleRegions.trim()) mark('eligibleRegions', 'Eligible Regions')
  else if (!eligibleRegionOptions.some((option) => option.value === form.eligibleRegions)) {
    mark('eligibleRegions', 'Eligible Regions')
  }
  if (!form.unlimitedRedemptions) {
    if (!form.maxRedemptions.trim()) {
      mark('maxRedemptions', 'Total Redemptions')
    } else {
      const max = Number.parseInt(form.maxRedemptions.trim(), 10)
      if (!Number.isFinite(max) || max <= 0) {
        mark('maxRedemptions', 'Total Redemptions')
      }
    }
  }
  if (!form.description.trim()) mark('description', 'Description')

  if (missing.length > 0) {
    const detail =
      missing.length <= 3 ? missing.join(', ') : `${missing.slice(0, 3).join(', ')} +${missing.length - 3} more`
    showToast('warning', `Please complete: ${detail}`)
    scrollToFirstError()
    return false
  }
  return true
}

function scrollToFirstError(): void {
  requestAnimationFrame(() => {
    const modalBody = document.querySelector('.create-coupon-modal .modal-body') as HTMLElement | null
    const firstError = document.querySelector(
      '.create-coupon-modal .input-wrapper.error, .create-coupon-modal .select-wrapper.error, .create-coupon-modal .upload-container.error, .create-coupon-modal .date-field.error, .create-coupon-modal .textarea-wrapper.error',
    ) as HTMLElement | null
    if (!firstError) return
    firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
    if (modalBody && firstError) {
      // keep focus near the field for visibility
      firstError.querySelector('input, textarea')?.dispatchEvent(new Event('focus'))
    }
  })
}

async function handleSubmit(): Promise<void> {
  // 防重复提交：连点会生成不同 code，复制会创建多张券
  if (submitting.value) return
  if (!copyDataReady.value) return
  if (!validateForm()) return

  submitting.value = true
  let succeeded = false
  try {
    // 与新建一致：提交用用户输入的 code（复制仅打开时自动填一个建议 code）
    const couponCode = form.code.trim()

    const response = await postCouponCreate({
      name: form.name.trim(),
      code: couponCode,
      picture: pictureFile.value,
      picture_url: pictureFile.value ? undefined : picturePathFromSource.value || undefined,
      discount_type: form.discountType,
      discount_amount: form.discountAmount.trim(),
      min_spend_not_required: form.minSpendNotRequired,
      min_spend: form.minSpendNotRequired ? '' : form.minSpend.trim(),
      start_type: form.startType,
      start_time: form.startType === 'specific_time' ? form.startTime : undefined,
      end_type: form.endType,
      end_time: form.endType === 'specific_time' ? form.endTime : undefined,
      eligible_services: [...form.eligibleServices],
      usage_limit: form.usageLimit,
      eligible_regions: form.eligibleRegions,
      max_redemptions: form.maxRedemptions.trim(),
      unlimited_redemptions: form.unlimitedRedemptions,
      description: form.description.trim(),
    })

    if (isCouponMutationSuccess(response)) {
      succeeded = true
      emit('saved')
      return
    }

    showToast('warning', readCouponApiMessage(response, 'Coupon code must be unique'))
  } catch (error) {
    console.error('Create coupon error:', error)
    const message =
      error instanceof Error && error.message.trim()
        ? error.message.trim()
        : 'Failed to create coupon'
    showToast('error', message)
    emit('failed')
  } finally {
    // 成功后保持锁，避免弹窗关闭前再次点出第二张券
    if (!succeeded) submitting.value = false
  }
}

function resetForm(): void {
  form.name = ''
  form.code = ''
  form.discountType = 'fixed_amount'
  form.discountAmount = ''
  form.minSpend = ''
  form.minSpendNotRequired = true
  form.startType = 'immediately'
  form.startTime = ''
  form.endType = 'specific_time'
  form.endTime = ''
  form.eligibleServices = resolveDefaultEligibleServices()
  form.usageLimit = 'one_per_account_or_address'
  form.eligibleRegions = 'one_per_account_or_address'
  form.maxRedemptions = ''
  form.unlimitedRedemptions = true
  form.description = ''

  Object.keys(errors).forEach((key) => {
    errors[key as keyof typeof errors] = false
  })

  pictureFile.value = null
  picturePreview.value = ''
  picturePathFromSource.value = ''
}

function applyInitialValues(): void {
  const values = props.initialValues
  if (!values) return

  form.name = values.name?.trim() || ''
  // 复制：initialValues 里已带建议 code；不再二次改写，允许用户手改后原样提交
  form.code = values.code
  form.discountType = values.discountType
  form.discountAmount = values.discountAmount
  form.minSpend = values.minSpend
  form.minSpendNotRequired = values.minSpendNotRequired
  form.startType = values.startType
  form.startTime = values.startTime
  form.endType = values.endType
  form.endTime = values.endTime
  form.eligibleServices = [...values.eligibleServices]
  form.usageLimit = values.usageLimit
  form.eligibleRegions = values.eligibleRegions
  form.maxRedemptions = values.maxRedemptions
  form.unlimitedRedemptions = values.unlimitedRedemptions
  form.description = values.description

  if (values.picturePreview) {
    picturePreview.value = values.picturePreview
  }
  picturePathFromSource.value = values.picturePath?.trim() || ''
}

async function loadEligibleServiceOptions(): Promise<void> {
  if (props.eligibleServiceOptions && props.eligibleServiceOptions.length > 0) {
    eligibleServiceOptionsList.value = [...props.eligibleServiceOptions]
    return
  }

  try {
    const response = await postCouponCenterMeta({})
    if (isCouponApiSuccess(response.code) && response.data) {
      eligibleServiceOptionsList.value = buildEligibleServices(response.data)
      return
    }
  } catch (error) {
    console.error('Failed to load eligible services meta:', error)
  }
  eligibleServiceOptionsList.value = buildEligibleServices(null)
}

function handleDocumentClick(event: MouseEvent): void {
  const target = event.target as HTMLElement
  if (
    target.closest('.custom-select') ||
    target.closest('.custom-dropdown') ||
    target.closest('.date-picker-pop') ||
    target.closest('.time-field')
  ) {
    return
  }
  closeAllDropdowns()
}

onMounted(() => {
  void (async () => {
    await loadEligibleServiceOptions()
    // 复制：若 detail 已回填则不要 reset 冲掉；仅在尚未拿到初始值时清空
    if (props.copyMode) {
      if (!props.initialValues) resetForm()
    } else if (!props.initialValues) {
      form.eligibleServices = resolveDefaultEligibleServices()
    }
    applyInitialValues()
  })()
  document.addEventListener('click', handleDocumentClick)
})

watch(
  () => props.initialValues,
  () => {
    applyInitialValues()
  },
)

onUnmounted(() => {
  document.removeEventListener('click', handleDocumentClick)
  if (picturePreview.value) URL.revokeObjectURL(picturePreview.value)
})
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
  padding: 20px;
}

.modal-container {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 530px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 24px 0;
  flex-shrink: 0;
}

.modal-title {
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  color: #000;
  margin: 0;
}

.close-btn {
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px 0;
}

.form-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  color: #000;
  margin: 0;
}

.form-label.required::before {
  content: '*';
  color: #e54848;
  margin-right: 4px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.form-row--redemptions,
.form-row--with-check {
  grid-template-columns: 1fr auto;
  align-items: center;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-wrapper.error {
  border: 1px solid #ff4d4f;
  border-radius: 100px;
  background: #fffafa;
}

.input-wrapper.disabled {
  opacity: 1;
}

.input-wrapper.disabled .form-input:disabled {
  background: #f5f6fb;
  color: #000000;
}

.form-input,
.custom-select {
  width: 100%;
  height: 36px;
  box-sizing: border-box;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    'PingFang SC',
    'PingFang SC Regular',
    PingFangSC,
    -apple-system,
    BlinkMacSystemFont,
    'Microsoft YaHei',
    sans-serif;
  color: #000000;
}

.form-input {
  padding: 8px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 100px;
  outline: none;
  background: #fff;
}

.form-input::placeholder,
.select-value.is-placeholder {
  color: #cccccc;
}

.form-input:focus {
  border-color: #666;
}

.input-wrapper.error .form-input {
  border: none;
  background: transparent;
}

.field-error-icon {
  position: absolute;
  right: 8px;
  width: 16px;
  height: 16px;
  pointer-events: none;
}

.input-suffix {
  position: absolute;
  right: 12px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  color: #000000;
  pointer-events: none;
}

.input-wrapper.has-suffix.error .input-suffix {
  right: 32px;
}

.form-input.form-input--with-suffix {
  padding-right: 40px;
}

.input-wrapper.has-suffix.error .form-input.form-input--with-suffix {
  padding-right: 56px;
}

.select-wrapper {
  position: relative;
}

.select-wrapper.error .custom-select {
  border-color: #ff4d4f;
  background: #fffafa;
}

.custom-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px 8px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 100px;
  background: #fff;
  cursor: pointer;
}

.custom-select.open {
  border-color: #666;
}

.select-value {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    'PingFang SC',
    'PingFang SC Regular',
    PingFangSC,
    -apple-system,
    BlinkMacSystemFont,
    'Microsoft YaHei',
    sans-serif;
  color: #000000;
}

.select-arrow {
  width: 16px;
  height: 16px;
  opacity: 0.55;
  flex-shrink: 0;
}

.custom-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10;
  max-height: 300px;
  overflow-y: auto;
  padding: 4px 0;
}

.checkbox-dropdown {
  min-width: 100%;
}

.dropdown-item {
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  color: #000000;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    'PingFang SC',
    'PingFang SC Regular',
    PingFangSC,
    -apple-system,
    BlinkMacSystemFont,
    'Microsoft YaHei',
    sans-serif;
}

.dropdown-item:hover {
  background-color: #f5f5f5;
}

.custom-dropdown .dropdown-item.active {
  font-weight: 600;
}

.custom-dropdown .dropdown-item.active span {
  font-weight: 600 !important;
}

.checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.checkbox {
  width: 18px;
  height: 18px;
  border: 2px solid #e0e0e0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s;
  cursor: pointer;
}

.checkbox.checked {
  background-color: #000000;
  border-color: #000000;
}

.check-svg {
  width: 12px;
  height: 12px;
}

.upload-container.error .upload-area {
  border-color: #ff4d4f;
  background: #fffafa;
}

.upload-area {
  width: 355px;
  height: 190px;
  max-width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  background: #fff;
  box-sizing: border-box;
}

.upload-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

.upload-text {
  font-size: 14px;
  color: #000;
}

.preview-area {
  position: relative;
  width: 355px;
  height: 190px;
  max-width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-sizing: border-box;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.preview-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border: none;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.preview-remove-icon {
  width: 10px;
  height: 10px;
  filter: brightness(0) invert(1);
}

.file-input {
  display: none;
}

.date-field {
  position: relative;
  display: flex;
  align-items: center;
}

.date-field.disabled {
  pointer-events: none;
}

.date-field.disabled .date-input {
  background: #f5f6fb;
  color: #cccccc;
}

.date-field.error .date-input {
  border-color: #ff4d4f;
  background: #fffafa;
}

.date-input {
  padding-right: 40px;
}

.calendar-btn {
  position: absolute;
  right: 8px;
  width: 22px;
  height: 22px;
  border: none;
  background: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.calendar-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.date-error-icon {
  right: 36px;
}

.check-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  white-space: nowrap;
}

.checkbox-native {
  position: absolute;
  opacity: 0;
  width: 16px;
  height: 16px;
  margin: 0;
}

.checkbox-ui {
  width: 16px;
  height: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 2px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #ffffff;
}

.checkbox-ui.checked {
  background: #000000;
  border-color: #000000;
}

.check-icon {
  width: 10px;
  height: 8px;
  object-fit: contain;
  display: block;
}

.check-option-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  color: #000000;
}

.textarea-wrapper {
  position: relative;
}

.textarea-wrapper.error .form-textarea {
  border-color: #ff4d4f;
  background: #fffafa;
}

.form-textarea {
  width: 482px;
  max-width: 100%;
  height: 160px;
  min-height: 160px;
  padding: 8px 16px 28px;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  resize: vertical;
  font-size: 14px;
  line-height: 20px;
  font-family:
    'PingFang SC',
    'PingFang SC Regular',
    PingFangSC,
    -apple-system,
    BlinkMacSystemFont,
    'Microsoft YaHei',
    sans-serif;
  color: #000000;
  outline: none;
  box-sizing: border-box;
}

.form-textarea::placeholder {
  color: #cccccc;
}

.char-count {
  position: absolute;
  right: 12px;
  bottom: 8px;
  font-size: 12px;
  color: #999;
}

.textarea-error-icon {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 16px;
  height: 16px;
}

.button-group {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 24px;
  flex-shrink: 0;
}

.btn-cancel,
.btn-submit {
  width: 80px;
  height: 32px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 24px;
  font-family:
    SFPro,
    'SF Pro',
    'SF Pro Text',
    -apple-system,
    BlinkMacSystemFont,
    system-ui,
    sans-serif;
  cursor: pointer;
  display: flex;
  align-items: center;
  border: none;
  box-sizing: border-box;
}

.btn-cancel {
  padding: 0 16px;
  font-weight: 700;
  text-align: left;
  justify-content: flex-start;
  background: #fff;
  border: 1px solid #000;
  color: #000;
}

.btn-cancel:hover {
  background: #000;
  color: #fff;
}

.btn-submit {
  padding: 0 16px;
  font-weight: 700;
  text-align: left;
  justify-content: flex-start;
  background: #000;
  color: #fff;
}

.btn-submit span {
  font-size: 14px;
  font-weight: 700;
  line-height: 24px;
  text-align: left;
}

.btn-submit:hover:not(:disabled) {
  background: #484848;
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  justify-content: center;
  padding: 0;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.date-picker-backdrop {
  position: fixed;
  inset: 0;
  z-index: 100002;
  background: rgba(0, 0, 0, 0.2);
}

.date-picker-pop {
  width: 394px;
  height: auto;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  padding: 28px 0 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

.date-picker-pop :deep(.date-picker-calendar.cal-mini) {
  width: 100%;
  flex-shrink: 0;
  border: none;
  box-shadow: none;
  background: transparent;
  padding: 0;
  border-radius: 0;
  --cal-cell-size: 40px;
  --cal-cell-gap: 8px;
}

.date-picker-pop :deep(.date-picker-calendar .cal-head) {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0 24px;
  margin: 0 0 16px;
  box-sizing: border-box;
  gap: 16px;
}

.date-picker-pop :deep(.date-picker-calendar .cal-head-month),
.date-picker-pop :deep(.date-picker-calendar .cal-head-year) {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 24px;
}

.date-picker-pop :deep(.date-picker-calendar .cal-head-month .cal-nav:first-of-type) {
  flex-shrink: 0;
}

.date-picker-pop :deep(.date-picker-calendar .cal-head-month-label) {
  flex: 1;
  min-width: 0;
  text-align: center;
}

.date-picker-pop :deep(.date-picker-calendar .cal-head-month .cal-nav:last-of-type) {
  flex-shrink: 0;
}

.date-picker-pop :deep(.date-picker-calendar .cal-head-year .cal-nav:first-of-type) {
  flex-shrink: 0;
}

.date-picker-pop :deep(.date-picker-calendar .cal-head-year-label) {
  flex: 1;
  min-width: 0;
  text-align: center;
}

.date-picker-pop :deep(.date-picker-calendar .cal-head-year .cal-nav:last-of-type) {
  flex-shrink: 0;
}

.date-picker-pop :deep(.date-picker-calendar .cal-head-month-label),
.date-picker-pop :deep(.date-picker-calendar .cal-head-year-label) {
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 14px;
  color: #000000;
}

.date-picker-pop :deep(.date-picker-calendar .cal-nav) {
  width: 16px;
  height: 16px;
}

.date-picker-pop :deep(.date-picker-calendar .cal-nav-ic) {
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
}

.date-picker-pop :deep(.date-picker-calendar .cal-weeks) {
  display: grid;
  box-sizing: border-box;
  width: calc(100% - 24px);
  margin: 0 12px 12px;
  grid-template-columns: repeat(7, 40px);
  justify-content: space-between;
  gap: 0;
}

.date-picker-pop :deep(.date-picker-calendar .cal-weeks span) {
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 14px;
  color: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 40px;
  height: 14px;
  padding: 0;
  text-align: center;
}

.date-picker-pop :deep(.date-picker-calendar .cal-grid) {
  display: grid;
  box-sizing: border-box;
  width: calc(100% - 24px);
  margin: 0 12px;
  grid-template-columns: repeat(7, 40px);
  justify-content: space-between;
  gap: 0;
  row-gap: 8px;
}

.date-picker-pop :deep(.date-picker-calendar .cal-cell) {
  font-family: SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 14px;
  color: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 40px;
  height: 40px;
  margin: 0;
  padding: 0;
  border-radius: 6px;
}

.date-picker-divider {
  border-top: 1px solid #e0e0e0;
  margin: 16px 24px 0;
  flex-shrink: 0;
}

.date-picker-pop :deep(.cal-cell.lead),
.date-picker-pop :deep(.cal-cell.trail) {
  color: #999999;
}

.date-picker-pop :deep(.cal-cell.cal-sel),
.date-picker-pop :deep(.cal-cell.cal-sel.lead),
.date-picker-pop :deep(.cal-cell.cal-sel.trail) {
  color: #ffffff;
  -webkit-text-fill-color: #ffffff;
}

.time-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 0 24px;
  flex-shrink: 0;
  box-sizing: border-box;
  overflow: visible;
}

.time-field {
  position: relative;
  flex: 1;
  min-width: 0;
}

.time-field.is-open {
  z-index: 20;
}

.time-select {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 36px;
  padding: 8px 12px 8px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 100px;
  background: #fff;
  cursor: pointer;
  box-sizing: border-box;
}

.time-select-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    'PingFang SC',
    'PingFang SC Regular',
    PingFangSC,
    -apple-system,
    BlinkMacSystemFont,
    'Microsoft YaHei',
    sans-serif;
  color: #000000;
}

.time-select-label.is-placeholder {
  color: #cccccc;
}

.time-select-arrow {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  opacity: 0.55;
  object-fit: contain;
  transition: transform 0.2s ease;
}

.time-select-arrow.is-open {
  transform: rotate(180deg);
}

.time-dropdown {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  z-index: 30;
  max-height: 240px;
  overflow-y: auto;
  padding: 8px 0;
}

.time-dropdown-item {
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
  color: #000000;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family:
    'PingFang SC',
    'PingFang SC Regular',
    PingFangSC,
    -apple-system,
    BlinkMacSystemFont,
    'Microsoft YaHei',
    sans-serif;
  transition: background-color 0.2s;
}

.time-dropdown-item:hover,
.time-dropdown-item.active {
  background-color: #f5f5f5;
}
</style>
